// Main API integration file - replaces JSON loading with Supabase API
const { productsApi } = require('./products.js')
const { ordersApi } = require('./orders.js')

/**
 * Main function to replace layers-config.json loading
 * This function maintains compatibility with existing frontend code
 * @returns {Promise<FrontendConfig>} Configuration data compatible with existing frontend
 */
async function loadConfiguration() {
  try {
    console.log('🚀 Loading configuration from Supabase instead of JSON files...')
    const config = await productsApi.getConfig()
    console.log('✅ Configuration loaded successfully from database')
    return config
  } catch (error) {
    console.error('💥 Error loading configuration from Supabase:', error)
    
    // Fallback to static JSON files if needed
    console.log('⚠️ Falling back to static JSON files...')
    try {
      const response = await fetch('/data/layers-config.json')
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const fallbackConfig = await response.json()
      console.log('✅ Fallback configuration loaded from JSON')
      return fallbackConfig
    } catch (fallbackError) {
      console.error('💥 Fallback also failed:', fallbackError)
      throw new Error('Failed to load configuration from both database and fallback')
    }
  }
}

/**
 * Function to create order and replace console.log in handleCheckout
 * @param {Object} formData - Customer form data
 * @param {Array} cartItems - Array of cart items
 * @param {number} totalPrice - Total order price
 * @returns {Promise<Object>} Created order
 */
async function submitOrder(formData, cartItems, totalPrice) {
  try {
    console.log('🔄 Submitting order to database...')
    
    // Transform cart items to match expected format
    const transformedItems = cartItems.map(item => ({
      id: item.id || `${Date.now()}-${Math.random()}`,
      name: item.name || `Матрас ${item.size || 'Custom'}`,
      price: item.price || 0,
      quantity: item.quantity || 1,
      configuration: {
        cover: item.cover || '',
        size: item.size || '',
        height: item.height || '',
        layer1: item.layer1 || '',
        layer2: item.layer2 || '',
        layer3: item.layer3 || ''
      }
    }))

    const order = await ordersApi.createOrder({
      customer: formData,
      items: transformedItems,
      total: totalPrice
    })
    
    console.log('✅ Order submitted successfully:', order.order_number)
    return order
  } catch (error) {
    console.error('💥 Failed to submit order:', error)
    throw error
  }
}

/**
 * Get order by order number for customer reference
 * @param {string} orderNumber - Order number
 * @returns {Promise<Object|null>} Order details
 */
async function getOrderByNumber(orderNumber) {
  try {
    return await ordersApi.getOrderByNumber(orderNumber)
  } catch (error) {
    console.error(`💥 Failed to get order ${orderNumber}:`, error)
    throw error
  }
}

/**
 * Calculate mattress price based on configuration
 * @param {Object} config - Mattress configuration
 * @returns {Promise<number>} Calculated price
 */
async function calculateMattressPrice(config) {
  try {
    return await productsApi.calculatePrice(config)
  } catch (error) {
    console.error('💥 Failed to calculate price:', error)
    throw error
  }
}

/**
 * Get layers available for specific height
 * @param {number} height - Mattress height
 * @returns {Promise<Array>} Available layers
 */
async function getLayersForHeight(height) {
  try {
    return await productsApi.getLayersForHeight(height)
  } catch (error) {
    console.error(`💥 Failed to get layers for height ${height}:`, error)
    throw error
  }
}

// Export all functions for use in React
module.exports = {
  loadConfiguration,
  submitOrder,
  getOrderByNumber,
  calculateMattressPrice,
  getLayersForHeight,
  productsApi,
  ordersApi
};